import { useRef, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import type { AiCountTag, AiMeasurementBasis } from "./ai-count-types";

const startLabels = { count: "Count selected items", LF: "Measure selected lengths", SQFT: "Measure selected areas" };

/** Debug-only: starts an ad-hoc page analysis outside the job's AI setup. */
export function AiCountStartForm({ jobId, planDocumentId, pageNumber, tags, enabled, busy, onAction }: {
  jobId: Id<"takeoffPilotJobs">;
  planDocumentId: Id<"takeoffPilotPlanDocuments">;
  pageNumber: number;
  tags: AiCountTag[];
  enabled: boolean | undefined;
  busy: boolean;
  onAction: (work: () => Promise<unknown>) => void;
}) {
  const start = useAction(api.takeoffPilot.tagPreview.startAiCount);
  const [selectedTags, setSelectedTags] = useState<AiCountTag["tagId"][]>([]);
  const [includeExisting, setIncludeExisting] = useState(false);
  const [measurementBasis, setMeasurementBasis] = useState<AiMeasurementBasis>("count");
  const request = useRef<string | null>(null);

  return (
    <div className="space-y-3">
      <p className="font-medium">Analyze more items on this page</p>
      <div className="space-y-3">
        <p className="text-muted-foreground">Runs a new analysis of page {pageNumber} for the chosen tags.</p>
        <fieldset disabled={busy} className="space-y-1">
          <legend className="font-medium">Item tags (up to 8)</legend>
          {tags.map((tag) => (
            <label key={tag.tagId} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedTags.includes(tag.tagId)}
                onChange={(event) => {
                  setSelectedTags((old) =>
                    event.target.checked
                      ? [...old, tag.tagId]
                      : old.filter((id) => id !== tag.tagId),
                  );
                  request.current = null;
                }}
              />
              {tag.rawValue}
            </label>
          ))}
          <label className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              checked={includeExisting}
              onChange={(event) => {
                setIncludeExisting(event.target.checked);
                request.current = null;
              }}
            />
            Include existing items to remain
          </label>
        </fieldset>
        <label className="block">
          Measurement basis{" "}
          <select
            aria-label="AI measurement basis"
            className="rounded-md border bg-background p-1"
            value={measurementBasis}
            onChange={(e) => {
              setMeasurementBasis(e.target.value as AiMeasurementBasis);
              request.current = null;
            }}
          >
            <option value="count">Count items</option>
            <option value="LF">Measure linear feet automatically</option>
            <option value="SQFT">Measure floor area (SQFT)</option>
          </select>
        </label>
        <Button
          disabled={
            busy ||
            !enabled ||
            selectedTags.length < 1 ||
            selectedTags.length > 8
          }
          onClick={() =>
            onAction(async () => {
              request.current ??= crypto.randomUUID();
              await start({
                jobId,
                planDocumentId,
                pageNumber,
                selectedTagIds: selectedTags,
                includeExisting,
                measurementBasis,
                requestKey: request.current,
              });
              request.current = null;
            })
          }
        >
          {startLabels[measurementBasis]}
        </Button>
      </div>
    </div>
  );
}
